import { Client, IMessage, IFrame } from '@stomp/stompjs';
import * as Sentry from '@sentry/react';
import { getRegisterDeviceOptionsForWebsocket } from '../helper/register-device';
import { MessagePayload, UUID } from '../types';
import { CONNECTION_FAILED } from '../config/constants';
import { checkBackendHealth } from '../../services/health.service';
import Messages from 'src/config/messages.constants';

export interface WebSocketClientOptions {
    url: string;
    debug?: (message: string) => void;
    authToken: string | null;
    userId: UUID;
    deviceId: UUID;
}

export type OnRegisterDeviceCallback = (isDeviceRegistered: boolean) => void;
export type OnMessageCallback = (data: any) => void;
export type OnErrorCallback = (error: {
    message: string;
    type: 'STOMP' | 'WEBSOCKET' | 'GENERIC';
}) => void;

const BLOCKED_ATTEMPT_THRESHOLD = 3;

export default class WebSocketClient {
    private client: Client | null = null;
    private authToken: string | null = null;
    private userId: UUID | null = null;
    private deviceId: UUID | null = null;
    private transportEverOpened = false;
    private failedAttempts = 0;

    private blockReported = false;
    private transportErrorNotified = false;

    private onRegisterDeviceCallback: OnRegisterDeviceCallback | null = null;
    private onMessageCallback: OnMessageCallback | null = null;
    private onErrorCallback: OnErrorCallback | null = null;

    constructor({ url, authToken, userId, deviceId, debug = () => {} }: WebSocketClientOptions) {
        this.userId = userId;
        this.authToken = authToken;
        this.deviceId = deviceId;

        this.client = new Client({
            brokerURL: url,
            debug: (message) => {
                debug(`${new Date().toUTCString()} | ${message}`);
            },
            reconnectDelay: 2000,
            heartbeatIncoming: 10000,
            heartbeatOutgoing: 10000,
            connectHeaders: this.getHeaders(),
        });

        this.client.beforeConnect = () => {
            if (this.client) {
                this.client.connectHeaders = this.getHeaders();
            }
        };
    }

    private getHeaders() {
        const headers: Record<string, string> = {
            'App-Name': 'userapp',
            'App-Source': 'userapp',
        };

        if (this.authToken) {
            headers['Authorization'] = this.authToken.startsWith('Bearer ')
                ? this.authToken
                : `Bearer ${this.authToken}`;
        }

        return headers;
    }

    onMessage(onMessageCallback: OnMessageCallback) {
        this.onMessageCallback = onMessageCallback;
    }

    onError(onErrorCallback: OnErrorCallback) {
        this.onErrorCallback = onErrorCallback;
    }

    onRegisterDevice(onRegisterDeviceCallback: OnRegisterDeviceCallback) {
        this.onRegisterDeviceCallback = onRegisterDeviceCallback;
    }

    private registerDevice() {
        if (!this.client || !this.userId || !this.deviceId) {
            this.onErrorCallback?.({ message: CONNECTION_FAILED, type: 'GENERIC' });
            return;
        }

        if (this.client.connected) {
            this.client.publish({
                destination: '/app/registerDesktopDevice',
                body: JSON.stringify(
                    getRegisterDeviceOptionsForWebsocket({
                        userId: this.userId,
                        deviceId: this.deviceId,
                    }),
                ),
            });

            this.onRegisterDeviceCallback?.(true);
        }
    }

    handleUnrecoverableStompError(serverErrorMessage: string) {
        Sentry.captureMessage(`Unrecoverable STOMP Error: ${serverErrorMessage}`, {
            level: Sentry.Severity.Error,
            tags: {
                userId: this.userId,
                unrecoverableStompError: true,
            },
        });

        this.forceShutdown();
    }

    private async handleUnexpectedDisconnect(
        closeData?: { code: number; wasClean: boolean },
        errorReason?: string,
    ) {
        if (this.blockReported) {
            return;
        }

        if (!this.transportEverOpened) {
            this.failedAttempts += 1;
        }

        let isBackendUp = true;
        const isOnline = navigator.onLine;

        if (isOnline) {
            isBackendUp = await checkBackendHealth();
        }

        const isBlockedByNetwork =
            !this.transportEverOpened &&
            this.failedAttempts >= BLOCKED_ATTEMPT_THRESHOLD &&
            isOnline &&
            isBackendUp;

        if (isBlockedByNetwork) {
            this.blockReported = true;

            this.onErrorCallback?.({ message: Messages.WEBSOCKET_BLOCKED, type: 'WEBSOCKET' });

            Sentry.captureMessage('WebSocket blocked by user network', {
                level: Sentry.Severity.Error,
                tags: {
                    userId: this.userId,
                    wsBlockedByUserNetwork: true,
                    connectionCloseCode: closeData?.code,
                },
                extra: { attempts: this.failedAttempts },
            });

            return;
        }

        if (closeData) {
            Sentry.captureMessage(errorReason || 'Web Socket connection closed', {
                tags: {
                    userId: this.userId,
                    connectionCloseCode: closeData.code,
                    cleanClosed: closeData.wasClean,
                    isConvoAPIServerDown: !isBackendUp,
                },
            });
        }
    }

    connect() {
        if (!this.client) {
            this.onErrorCallback?.({ message: CONNECTION_FAILED, type: 'GENERIC' });
            return;
        }

        this.client.onWebSocketError = () => {
            if (this.transportErrorNotified) {
                return;
            }

            this.transportErrorNotified = true;
            this.onErrorCallback?.({ message: CONNECTION_FAILED, type: 'WEBSOCKET' });
        };

        this.client.onWebSocketClose = (data) => {
            if (data.code !== 1000) {
                this.handleUnexpectedDisconnect(data);
            }
        };

        this.client.onStompError = (frame: IFrame) => {
            let serverErrorMessage = frame.headers['message'] || 'STOMP protocol error';

            // 1. Sanitize the carriage return artifacts
            serverErrorMessage = serverErrorMessage.replace(/\\c/g, '').trim();

            console.error('STOMP Protocol Error Received From Backend:', serverErrorMessage);

            this.onErrorCallback?.({
                message: serverErrorMessage,
                type: 'STOMP',
            });

            this.handleUnexpectedDisconnect(
                { code: 4001, wasClean: false },
                `STOMP Error: ${serverErrorMessage}`,
            );
        };

        this.client.onConnect = () => {
            this.transportEverOpened = true;
            this.failedAttempts = 0;
            this.blockReported = false;
            this.transportErrorNotified = false;

            this.subscribe();
            this.registerDevice();
        };

        this.client.activate();
    }

    private handleSubscriptionMessage(message: IMessage) {
        const { body } = message;

        if (body) {
            try {
                const messagePayload = JSON.parse(body) as MessagePayload;
                if (messagePayload?.data) {
                    this.onMessageCallback?.(messagePayload.data);
                }
            } catch (e) {
                console.error('Failed parsing message frame payload', e);
            }
        }
    }

    private subscribe() {
        if (this.client && this.userId && this.deviceId) {
            this.client.subscribe(`/user/${this.userId}/${this.deviceId}/notification`, (message) =>
                this.handleSubscriptionMessage(message),
            );
        }
    }

    destroy() {
        if (this.client) {
            this.client.onConnect = () => {};
            this.client.onStompError = () => {};
            this.client.onWebSocketClose = () => {};
            this.client.onWebSocketError = () => {};
            this.client.beforeConnect = () => {};

            this.client.deactivate({ force: true });
        }

        this.client = null;
        this.userId = null;
        this.authToken = null;
        this.onMessageCallback = null;
        this.onErrorCallback = null;
        this.onRegisterDeviceCallback = null;
    }

    forceShutdown() {
        if (this.client) {
            // Kill the auto-reconnect delay ONLY for this specific forced shutdown
            this.client.reconnectDelay = 0;

            this.client.onConnect = () => {};
            this.client.onStompError = () => {};
            this.client.onWebSocketClose = () => {};
            this.client.onWebSocketError = () => {};
            this.client.beforeConnect = () => {};

            this.client.deactivate({ force: true });
        }

        this.client = null;
        this.userId = null;
        this.authToken = null;
        this.onMessageCallback = null;
        this.onErrorCallback = null;
        this.onRegisterDeviceCallback = null;
    }
}
